// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import JettinAppActions from "@stoneage/App/Components/JettingAppComponents/JettinAppActions";
import JettinAppButton from "@stoneage/App/Components/JettingAppComponents/JettinAppButton";
import JettingAppCheckBox from "@stoneage/App/Components/JettingAppComponents/JettingAppCheckBox";
import JettingAppDropDown from "@stoneage/App/Components/JettingAppComponents/JettingAppDropDown";
import JettinAppGraph from "@stoneage/App/Components/JettingAppComponents/JettingAppGraph";
import JettingAppHoses from "@stoneage/App/Components/JettingAppComponents/JettingAppHoses";
import JettinAppImage from "@stoneage/App/Components/JettingAppComponents/JettingAppImage";
import JettingAppJetting from "@stoneage/App/Components/JettingAppComponents/JettingAppJetting";
import JettingAppRadio from "@stoneage/App/Components/JettingAppComponents/JettingAppRadio";
import JettingAppResult from "@stoneage/App/Components/JettingAppComponents/JettingAppResult";
import JettingAppSlider from "@stoneage/App/Components/JettingAppComponents/JettingAppSlider";
import JettingAppTextField from "@stoneage/App/Components/JettingAppComponents/JettingAppTextField";
import JettingAppToggle from "@stoneage/App/Components/JettingAppComponents/JettingAppToggle";
import JettingAppUnitsToggle from "@stoneage/App/Components/JettingAppComponents/JettingAppUnitsToggle";
import Notify from "@stoneage/App/Components/Notify";
import { Calculator, Control, JetArray, Panel } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { inputTypes, pressureUnits } from "@stoneage/App/Widgets/JettingAppView/JettingAppView";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { parseBoolean, xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

export interface JettingAppPanelsContainerStyles {
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    calculatorTitle?: TypographyPresentationProps;
    panelsContainer?: InjectableCss;
    panelWrapper?: InjectableCss;
    panelTitle?: InjectableCss;
    sectionsWrapper?: InjectableCss;
    section?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    control?: InjectableCss;
    divider?: InjectableCss;
    smallDivider?: InjectableCss;
    unitSelectorWrapper?: InjectableCss;
    resetLinkWrapper?: InjectableCss;
    resetLink?: InjectableCss;
    sectionTitleWrapper?: InjectableCss;
    tab?: ButtonPresentationProps;
    tabsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    nextButton?: ButtonPresentationProps;
    suggestJettingSection?: InjectableCss;
    panelContainer?: InjectableCss;
    htmlContent?: InjectableCss;
    verifySetup?: ButtonPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const jettingAppPanelsContainerStyles: JettingAppPanelsContainerStyles = {};
const styles = jettingAppPanelsContainerStyles;

type PanelsContainerProps = {
    tab: Calculator;
    panels: Panel[];
    selectedCalculator: Calculator | null;
    handleInputChange: any;
    handleButtonClick: any;
    handleHosesChange: any;
    handleJetChange: any;
    isLoadingPanels: boolean;
    selectedUnit: string;
    changeUnit: any;
    changePressureUnit: any;
    pressureUnit: pressureUnits;
    acceptConfigButtonDisabled: boolean;
    handleResetAll: any;
    onlyCalculateOnButton: boolean;
};

const PanelsContainer = ({
    tab,
    panels,
    selectedCalculator,
    handleInputChange,
    handleButtonClick,
    handleHosesChange,
    handleJetChange,
    isLoadingPanels,
    selectedUnit,
    changeUnit,
    changePressureUnit,
    pressureUnit,
    acceptConfigButtonDisabled,
    handleResetAll,
    onlyCalculateOnButton,
}: PanelsContainerProps) => {
    const dispatch = useDispatch();
    const [selectedPanelIndex, setSelectedPanelIndex] = useState(0);
    const {
        isTabletScreen,
        acceptConfigControl,
        hideOtherPanels,
        hasVerifySetupButton,
        verifySetupControl,
        jettingAppUnit,
    } = useAppSelector(state => {
        return {
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
            acceptConfigControl: (state as CustomAppState).xJettingAppCalculators.acceptConfigControl,
            verifySetupControl: (state as CustomAppState).xJettingAppCalculators.verifySetupControl,
            hideOtherPanels: (state as CustomAppState).xJettingAppCalculators.hideOtherPanels,
            hasVerifySetupButton: (state as CustomAppState).xJettingAppCalculators.hasVerifySetupButton,
            jettingAppUnit: getCookie("JettingAppUnit") || "US",
        };
    });

    const getComponent = (control: Control, panelIndex: number, sectionIndex: number, controlIndex: number) => {
        switch (control.type) {
            case inputTypes.numericBox:
                return (
                    <>
                        {control.readOnly ? (
                            <JettingAppResult
                                label={control.label || ""}
                                value={control.value}
                                tooltipText={control.toolTip}
                                currentUnit={control.units}
                                message={control.message}
                                shouldLabelTakeHalfWidth={!onlyCalculateOnButton}
                            />
                        ) : (
                            <JettingAppTextField
                                label={control.label || ""}
                                value={Number(control.value || 0)}
                                tooltipText={control.toolTip}
                                currentUnit={control.units}
                                units={control.additionalInfo}
                                message={control.message}
                                onChange={(value: any) =>
                                    handleInputChange(value, panelIndex, sectionIndex, controlIndex)
                                }
                                labelIsHorizontal={true}
                                updateFieldImmediately={onlyCalculateOnButton}
                                readOnly={control.readOnly}
                            />
                        )}
                    </>
                );
            case inputTypes.button:
                // eslint-disable-next-line no-case-declarations
                let disabled = false;
                if (control.additionalInfo?.["ControlType"] === inputTypes.acceptConfig) {
                    disabled = acceptConfigButtonDisabled;
                }
                // we need to hide the verify setup button after displaying all panels
                if (
                    control.additionalInfo?.["ControlType"] === inputTypes.verifySetup &&
                    (!hideOtherPanels || isTabletScreen)
                ) {
                    return null;
                }
                return (
                    <JettinAppButton
                        label={control.label || ""}
                        readOnly={control.readOnly}
                        onButtonClick={() => handleButtonClick(control.additionalInfo)}
                        width={control.width || 100}
                        tooltipText={control.toolTip}
                        disabled={disabled}
                    />
                );
            case inputTypes.divider:
                return <StyledWrapper {...styles.divider} />;
            case inputTypes.smallDivider:
                return <StyledWrapper className="small-divider" {...styles.smallDivider} />;

            case inputTypes.image:
                return <JettinAppImage tooltipText={control.toolTip} url={control?.additionalInfo?.["URL"]} />;
            case inputTypes.dropDown:
                return (
                    <JettingAppDropDown
                        label={control.label || ""}
                        tooltipText={control.toolTip}
                        value={control.value}
                        options={control.options}
                        labelIsHorizontal={false}
                        readOnly={control.readOnly}
                        onChange={(event: any) =>
                            handleInputChange(event.target.value, panelIndex, sectionIndex, controlIndex)
                        }
                        currentUnit={control.units}
                    />
                );
            case inputTypes.slider:
                return (
                    <JettingAppSlider
                        step={control.step}
                        min={control.minValue}
                        max={control.maxValue}
                        label={control.label || ""}
                        value={Number(control.value)}
                        tooltipText={control.toolTip}
                        currentUnit={control.units}
                        units={control.additionalInfo}
                        message={control.message}
                        readOnly={control.readOnly}
                        onChange={(value: number) => handleInputChange(value, panelIndex, sectionIndex, controlIndex)}
                    />
                );

            case inputTypes.graph:
                return (
                    <JettinAppGraph
                        label={control.label || ""}
                        chartData={control.chartData}
                        tooltipText={control.toolTip}
                    />
                );
            case inputTypes.radio:
                return (
                    <JettingAppRadio
                        value={control.value?.toString()}
                        options={control.options || []}
                        label={control.label || ""}
                        tooltipText={control.toolTip}
                        readOnly={control.readOnly}
                        onChange={(value: string) => handleInputChange(value, panelIndex, sectionIndex, controlIndex)}
                    />
                );
            case inputTypes.hoses:
                return (
                    <JettingAppHoses
                        tooltipText={control.toolTip}
                        units={control.additionalInfo}
                        currentUnit={control.units}
                        hoseArray={control.hoseArray}
                        hoseOptions={control.hoseOptions}
                        onChange={updatedHoseArray =>
                            handleHosesChange(updatedHoseArray, panelIndex, sectionIndex, controlIndex)
                        }
                        message={control.message}
                        readOnly={control.readOnly}
                    />
                );
            case inputTypes.textBox:
                return (
                    <>
                        <JettingAppResult
                            label={control.label || ""}
                            value={control.value}
                            tooltipText={control.toolTip}
                            currentUnit={control.units}
                            message={control.message}
                            shouldLabelTakeHalfWidth={!onlyCalculateOnButton}
                        />
                    </>
                );
            case inputTypes.jetting:
                return (
                    <JettingAppJetting
                        onChange={(jetUpdatedArray: JetArray[]) =>
                            handleJetChange(jetUpdatedArray, panelIndex, sectionIndex, controlIndex)
                        }
                        jetOptions={control.jetOptions}
                        jetArray={control.jetArray}
                        tooltipText={control.toolTip}
                        readOnly={control.readOnly}
                    />
                );
            case inputTypes.checkBox:
                return (
                    <JettingAppCheckBox
                        label={control.label || ""}
                        value={control.value}
                        tooltipText={control.toolTip}
                        onChange={(value: any) => handleInputChange(value, panelIndex, sectionIndex, controlIndex)}
                    />
                );
            case inputTypes.htmlContent:
                return (
                    <StyledWrapper {...styles.htmlContent}>
                        {parse(control.additionalInfo?.Content || "", xParserOptions)}
                    </StyledWrapper>
                );
            case inputTypes.toggle:
                return (
                    <JettingAppToggle
                        label={control.label}
                        readonly={control.readOnly}
                        tooltipText={control.toolTip}
                        message={control.message}
                        options={[
                            { label: control.additionalInfo.TrueLabel, value: true },
                            { label: control.additionalInfo.FalseLabel, value: false },
                        ]}
                        value={!!control.value}
                        onChange={(value: any) => handleInputChange(value, panelIndex, sectionIndex, controlIndex)}
                    />
                );
            default:
                return null;
        }
    };

    const handleSelectedPanelChange = (index: number) => {
        setSelectedPanelIndex(index);
    };

    let filteredPanels = isTabletScreen ? [panels[selectedPanelIndex]] : panels;
    // when we have verify setup button, we need to hide other panels until the button is clicked
    if (hideOtherPanels && hasVerifySetupButton) {
        filteredPanels = [panels[0]];
    }
    return (
        <>
            {panels?.length > 0 && (
                <StyledWrapper {...styles.unitSelectorWrapper}>
                    <JettingAppUnitsToggle
                        units={[translate("US"), translate("Metric")]}
                        unit={selectedUnit}
                        onButtonClick={changeUnit}
                    />
                </StyledWrapper>
            )}

            <Typography {...styles.calculatorTitle}>{selectedCalculator?.title}</Typography>
            {isLoadingPanels ? (
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <>
                    {isTabletScreen && (
                        <>
                            <StyledWrapper {...styles.tabsWrapper}>
                                {panels.map((panel, index) => {
                                    return (
                                        <>
                                            <Button
                                                sizeVariant="small"
                                                onClick={() => handleSelectedPanelChange(index)}
                                                showIcon={false}
                                                variant={index === selectedPanelIndex ? "primary" : "secondary"}
                                                disabled={index > 0 && hideOtherPanels && hasVerifySetupButton}
                                                {...styles.tab}
                                            >
                                                {panel.title}
                                            </Button>
                                        </>
                                    );
                                })}
                            </StyledWrapper>
                        </>
                    )}
                    <StyledWrapper {...styles.panelsContainer} key={tab?.calculatorId}>
                        {panels && panels.length && filteredPanels?.length ? (
                            filteredPanels?.map((panel, panelIndex) => {
                                return (
                                    <StyledWrapper
                                        {...styles.panelContainer}
                                        {...{ numberOfPanels: panels.length }}
                                        key={panel.panelID}
                                    >
                                        <StyledWrapper {...styles.panelWrapper}>
                                            <StyledWrapper {...styles.panelTitle}>{panel.title}</StyledWrapper>
                                            <StyledWrapper {...styles.sectionsWrapper}>
                                                {panel?.sections?.map((section, sectionIndex) => {
                                                    if (!section.controls) {
                                                        return null;
                                                    }

                                                    return (
                                                        <StyledWrapper key={section.sectionId} {...styles.section}>
                                                            {(section.displayFlowConverter ||
                                                                section.title ||
                                                                section.toolTip) && (
                                                                <StyledWrapper {...styles.sectionTitleWrapper}>
                                                                    {(section.title || section.toolTip) && (
                                                                        <Typography {...styles.sectionTitle}>
                                                                            {section.title}
                                                                            {section.toolTip && (
                                                                                <Tooltip
                                                                                    {...styles.tooltip}
                                                                                    passAsChildren={true}
                                                                                >
                                                                                    <Typography>
                                                                                        {parse(
                                                                                            section.toolTip,
                                                                                            xParserOptions,
                                                                                        )}
                                                                                    </Typography>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                    {section.displayFlowConverter && (
                                                                        <JettingAppUnitsToggle
                                                                            units={[
                                                                                translate(pressureUnits.gpm),
                                                                                translate(pressureUnits.lpm),
                                                                            ]}
                                                                            unit={pressureUnit}
                                                                            onButtonClick={() =>
                                                                                changePressureUnit(
                                                                                    Number(panel.panelID || 1) - 1,
                                                                                    sectionIndex,
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </StyledWrapper>
                                                            )}

                                                            {section.controls?.map((control, controlIndex) => {
                                                                if (
                                                                    isTabletScreen &&
                                                                    control.additionalInfo?.["ControlType"] ===
                                                                        inputTypes.acceptConfig
                                                                ) {
                                                                    return null;
                                                                }
                                                                return (
                                                                    <StyledWrapper
                                                                        key={`${panel.panelID}_${section.sectionId}_${control.controlId}`}
                                                                        {...{
                                                                            fieldWidth: control.width,
                                                                        }}
                                                                        {...styles.control}
                                                                    >
                                                                        {getComponent(
                                                                            control,
                                                                            Number(panel.panelID || 1) - 1,
                                                                            sectionIndex,
                                                                            controlIndex,
                                                                        )}
                                                                    </StyledWrapper>
                                                                );
                                                            })}
                                                        </StyledWrapper>
                                                    );
                                                })}
                                            </StyledWrapper>

                                            {panel.resetAll && (
                                                <StyledWrapper {...styles.resetLinkWrapper}>
                                                    <Link onClick={handleResetAll} {...styles.resetLink}>
                                                        {translate("Reset All to Default")}
                                                    </Link>
                                                </StyledWrapper>
                                            )}

                                            {((isTabletScreen && selectedPanelIndex === panels.length - 1) ||
                                                panelIndex === panels.length - 1) && (
                                                <>
                                                    <StyledWrapper {...styles.divider} />
                                                    <JettinAppActions
                                                        calculatorId={selectedCalculator?.calculatorId || ""}
                                                        panels={panels}
                                                        units={jettingAppUnit}
                                                        onlyCalculateOnButton={onlyCalculateOnButton}
                                                    />
                                                </>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })
                        ) : (
                            <Notify messageType="warning">
                                {siteMessage("Custom_JettingApp_CurrentCalculatorDoesntHavePanelsSet")}
                            </Notify>
                        )}
                    </StyledWrapper>
                    {/* when onlyCalculateOnButton is true that means its a pressure calculators,
                     so we need to hide these buttons on mobile */}
                    {isTabletScreen && panels?.length > 0 && !onlyCalculateOnButton && (
                        <StyledWrapper {...styles.buttonsWrapper}>
                            {selectedPanelIndex !== 0 && (
                                <Button
                                    showIcon={false}
                                    variant="secondary"
                                    sizeVariant="small"
                                    onClick={() => handleSelectedPanelChange(selectedPanelIndex - 1)}
                                >
                                    {translate("Previous")}
                                </Button>
                            )}
                            {selectedPanelIndex !== panels.length - 1 && !hideOtherPanels && (
                                <Button
                                    {...styles.nextButton}
                                    sizeVariant="small"
                                    showIcon={false}
                                    onClick={() => handleSelectedPanelChange(selectedPanelIndex + 1)}
                                >
                                    {translate("Next:")} {panels[selectedPanelIndex + 1].title}
                                </Button>
                            )}
                            {selectedPanelIndex === panels.length - 1 && acceptConfigControl && !hideOtherPanels && (
                                <Button
                                    {...styles.nextButton}
                                    sizeVariant="small"
                                    showIcon={false}
                                    disabled={acceptConfigButtonDisabled}
                                    onClick={() => handleButtonClick(acceptConfigControl?.additionalInfo)}
                                >
                                    {acceptConfigControl.label}
                                </Button>
                            )}

                            {hideOtherPanels && verifySetupControl?.additionalInfo && verifySetupControl?.label && (
                                <Button
                                    {...styles.verifySetup}
                                    sizeVariant="small"
                                    showIcon={false}
                                    onClick={() => handleButtonClick(verifySetupControl.additionalInfo)}
                                >
                                    {verifySetupControl.label}
                                </Button>
                            )}
                        </StyledWrapper>
                    )}
                </>
            )}
        </>
    );
};

export default PanelsContainer;
